import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Share2, Copy, Check, ExternalLink, Lock, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/contexts/auth.context";
import {
	ShareableLinkService,
	type ShareableLink,
} from "@/lib/podcast/shareableLink.service";

interface ShareButtonProps {
	chapterId?: string;
	chapterTitle?: string;
	podcastId?: string;
	podcastTitle?: string;
	variant?: "default" | "outline" | "ghost";
	size?: "default" | "sm" | "lg" | "icon";
}

export function ShareButton({
	chapterId,
	chapterTitle,
	podcastId,
	podcastTitle,
	variant = "ghost",
	size = "icon",
}: ShareButtonProps) {
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [shareType, setShareType] = useState<"chapter" | "podcast">(
		chapterId ? "chapter" : "podcast",
	);
	const [chapterLink, setChapterLink] = useState<ShareableLink | null>(null);
	const [podcastLink, setPodcastLink] = useState<ShareableLink | null>(null);
	const [loading, setLoading] = useState(false);
	const [copied, setCopied] = useState(false);

	const canShareChapter = user?.limits?.features.chapterSharing ?? true;
	const canSharePodcast = user?.limits?.features.wholePodcastSharing ?? false;

	const currentLink = shareType === "chapter" ? chapterLink : podcastLink;

	const handleCreateShareLink = async (type: "chapter" | "podcast") => {
		try {
			setLoading(true);

			const link =
				type === "chapter"
					? await ShareableLinkService.createChapterShareLink(chapterId!)
					: await ShareableLinkService.createPodcastShareLink(podcastId!);

			if (type === "chapter") {
				setChapterLink(link);
			} else {
				setPodcastLink(link);
			}

			toast.success(t("share.createLinkSuccess"));
		} catch (error) {
			console.error("Error creating share link:", error);
			if (error instanceof Error && error.message.includes("plan")) {
				toast.error(t("share.planRestriction"));
			} else {
				toast.error(t("share.createLinkFailed"));
			}
		} finally {
			setLoading(false);
		}
	};

	const handleCopyLink = async () => {
		if (!currentLink?.shareUrl) return;

		try {
			await ShareableLinkService.copyShareUrl(currentLink.shareUrl);
			setCopied(true);
			toast.success(t("share.copySuccess"));
			setTimeout(() => setCopied(false), 2000);
		} catch (error) {
			console.error("Error copying link:", error);
			toast.error(t("share.copyFailed"));
		}
	};

	const handleShare = async () => {
		if (!currentLink?.shareUrl) return;

		const shareTitle = shareType === "chapter" ? chapterTitle : podcastTitle;
		const shareText = t("share.shareDescription", { title: shareTitle });

		// Check if Web Share API is available (primarily on mobile)
		if (navigator.share) {
			try {
				await navigator.share({
					title: shareTitle || "PodHive",
					text: shareText,
					url: currentLink.shareUrl,
				});
				toast.success(t("share.shareSuccess"));
			} catch (error) {
				// User cancelled the share or an error occurred
				if ((error as Error).name !== "AbortError") {
					console.error("Error sharing:", error);
					// Fall back to copying
					await handleCopyLink();
				}
			}
		} else {
			// Fall back to copying if Web Share API is not available
			await handleCopyLink();
		}
	};

	const handleOpenLink = () => {
		if (!currentLink?.shareUrl) return;
		window.open(currentLink.shareUrl, "_blank");
	};

	const handleOpenDialog = async () => {
		setOpen(true);
		// Auto-create link for the current type if it doesn't exist
		if (shareType === "chapter" && !chapterLink && canShareChapter) {
			await handleCreateShareLink("chapter");
		} else if (shareType === "podcast" && !podcastLink && canSharePodcast) {
			await handleCreateShareLink("podcast");
		}
	};

	const handleTabChange = async (value: string) => {
		const newType = value as "chapter" | "podcast";
		setShareType(newType);
		setCopied(false);

		// Auto-create link if it doesn't exist and user has permission
		if (newType === "chapter" && !chapterLink && canShareChapter) {
			await handleCreateShareLink("chapter");
		} else if (newType === "podcast" && !podcastLink && canSharePodcast) {
			await handleCreateShareLink("podcast");
		}
	};

	const handleUpgradeClick = () => {
		setOpen(false);
		navigate("/pricing");
	};

	// Show lock if user can't share anything
	if (!canShareChapter && !canSharePodcast) {
		return (
			<Button
				variant={variant}
				size={size}
				onClick={() => toast.error(t("share.planRestriction"))}
				title={t("share.tooltips.upgradeToShare")}
				className="opacity-50"
			>
				<Lock className="h-4 w-4" />
			</Button>
		);
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button
					variant={variant}
					size={size}
					onClick={handleOpenDialog}
					title={t("share.tooltips.share")}
				>
					<Share2 className="h-4 w-4" />
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("share.shareContent")}</DialogTitle>
					<DialogDescription>
						{t("share.shareDescription", {
							title: shareType === "chapter" ? chapterTitle : podcastTitle,
						})}
					</DialogDescription>
				</DialogHeader>

				<Tabs
					value={shareType}
					onValueChange={handleTabChange}
					className="w-full"
				>
					<TabsList className="grid w-full grid-cols-2">
						<TabsTrigger
							value="chapter"
							disabled={!chapterId || !canShareChapter}
						>
							{t("share.chapter")}
						</TabsTrigger>
						<TabsTrigger
							value="podcast"
							disabled={!podcastId}
							className="relative"
						>
							{t("share.wholePodcast")}
							{!canSharePodcast && (
								<Crown className="h-3 w-3 ml-1 text-yellow-500" />
							)}
						</TabsTrigger>
					</TabsList>

					<TabsContent value="chapter" className="space-y-4 mt-4">
						{!canShareChapter ? (
							<div className="py-8 text-center space-y-3">
								<Lock className="h-12 w-12 mx-auto text-gray-400" />
								<p className="text-sm text-gray-600">
									{t("share.upgradeRequired")}
								</p>
								<Button
									variant="outline"
									size="sm"
									onClick={handleUpgradeClick}
								>
									<Crown className="h-4 w-4 mr-2" />
									{t("share.upgradePlan")}
								</Button>
							</div>
						) : loading && !chapterLink ? (
							<div className="flex items-center justify-center py-8">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
							</div>
						) : chapterLink ? (
							<ShareLinkDisplay
								link={chapterLink}
								onCopy={handleCopyLink}
								onShare={handleShare}
								onOpen={handleOpenLink}
								copied={copied}
								t={t}
							/>
						) : (
							<div className="py-4 text-center text-gray-500">
								{t("share.failedToCreate")}
							</div>
						)}
					</TabsContent>

					<TabsContent value="podcast" className="space-y-4 mt-4">
						{!canSharePodcast ? (
							<div className="py-8 text-center space-y-3">
								<div className="relative inline-block">
									<Lock className="h-12 w-12 mx-auto text-gray-400" />
									<Crown className="h-6 w-6 absolute -top-1 -right-1 text-yellow-500" />
								</div>
								<p className="text-sm text-gray-600">
									{t("share.premiumFeature")}
								</p>
								<p className="text-xs text-gray-500">
									{t("share.upgradeToPremium")}
								</p>
								<Button
									variant="outline"
									size="sm"
									onClick={handleUpgradeClick}
								>
									<Crown className="h-4 w-4 mr-2" />
									{t("share.upgradePlan")}
								</Button>
							</div>
						) : loading && !podcastLink ? (
							<div className="flex items-center justify-center py-8">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
							</div>
						) : podcastLink ? (
							<ShareLinkDisplay
								link={podcastLink}
								onCopy={handleCopyLink}
								onShare={handleShare}
								onOpen={handleOpenLink}
								copied={copied}
								t={t}
							/>
						) : (
							<div className="py-4 text-center text-gray-500">
								{t("share.failedToCreate")}
							</div>
						)}
					</TabsContent>
				</Tabs>
			</DialogContent>
		</Dialog>
	);
}

// Reusable component for displaying share link details
function ShareLinkDisplay({
	link,
	onCopy,
	onShare,
	onOpen,
	copied,
	t,
}: {
	link: ShareableLink;
	onCopy: () => void;
	onShare: () => void;
	onOpen: () => void;
	copied: boolean;
	t: any;
}) {
	return (
		<div className="space-y-4">
			{/* Share URL */}
			<div className="space-y-2">
				<Label htmlFor="share-url">{t("share.shareLink")}</Label>
				<div className="flex gap-2">
					<Input
						id="share-url"
						value={link.shareUrl}
						readOnly
						className="flex-1"
					/>
					<Button type="button" size="icon" variant="outline" onClick={onCopy}>
						{copied ? (
							<Check className="h-4 w-4 text-green-600" />
						) : (
							<Copy className="h-4 w-4" />
						)}
					</Button>
					<Button
						type="button"
						size="icon"
						variant="outline"
						onClick={onOpen}
						title={t("share.openInNewTab")}
					>
						<ExternalLink className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{/* Stats */}
			<div className="flex items-center justify-end text-sm text-gray-600">
				<span>
					{t("share.createdDate", {
						date: new Date(link.createdAt).toLocaleDateString(),
					})}
				</span>
			</div>

			{/* Status */}
			<div className="flex items-center gap-2">
				<div
					className={`h-2 w-2 rounded-full ${link.isActive ? "bg-green-500" : "bg-gray-400"}`}
				/>
				<span className="text-sm text-gray-600">
					{link.isActive ? t("share.active") : t("share.inactive")}
				</span>
			</div>

			{/* Actions */}
			<div className="flex gap-2 pt-4">
				<Button onClick={onShare} className="flex-1" variant="default">
					<Share2 className="h-4 w-4 mr-2" />
					{t("share.share")}
				</Button>
			</div>
		</div>
	);
}
